import styled from '@emotion/styled'
import { createPropFilter, WebLinkProps } from '@news-mono/web-common'
import {
    StyledLiveStatus,
    StyledPulsatingDotLogo,
} from '../../bauhaus/BauhausHeroCard/BauhausHeroCard.styled'
import { StyledLink as CardTextInteractions } from '../../cards/CardText/CardTextInteractions.styled'
import { EditorialLabel } from '../../typography/EditorialLabel/EditorialLabel'
import { StyledLiveBubble } from '../../typography/EditorialLabel/EditorialLabel.styled'
import { fonts } from '../../__styling'
import { colors } from '../../__styling/settings/colors'
import { metrics, ThemeMargins } from '../../__styling/settings/metrics'
import {
    breakpoint,
    breakpointBetween,
    breakpointMax,
} from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { breakWords } from '../../__styling/style-mixins'
import { CardMedia, MediaMode } from '../CardMedia/CardMedia'
import { TeaserMode } from '../CardText/CardTeaser'
import { CardText } from '../CardText/CardText'
import { StyledHeadlineText, StyledTeaser } from '../CardText/CardText.styled'
import { StyledKicker, StyledKickerText } from '../Kicker/Kicker.styled'
import { BauhausCardOrientationOptions, CardType } from './BauhausCard'

export interface StyledBauhausCardProps {
    hasBackground?: boolean
    hasSeparator?: boolean
    hasPadding?: boolean
    mediaMode?: MediaMode
    verticalSpacing?: keyof ThemeMargins
    isLarge?: boolean
    cardOrientation?: BauhausCardOrientationOptions
    fullWidthOnMobile?: boolean
}

export const StyledBauhausCard = styled('div')<StyledBauhausCardProps>(
    {
        ...breakWords,
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: 1,
        alignItems: 'stretch',
        borderRadius: '3px',
        lineHeight: '19px',
        boxShadow: `0 -1px 8px rgba(0,0,0,0.1)`,
    },
    ({
        verticalSpacing,
        theme,
        hasBackground,
        fullWidthOnMobile,
        hasSeparator,
        hasPadding,
    }) => ({
        marginBottom: verticalSpacing && theme.margins[verticalSpacing],
        borderBottom: hasSeparator
            ? `1px solid ${colors.sevennews.greyMarble}`
            : undefined,
        background: hasBackground
            ? theme.colors.background.secondary
            : theme.colors.background.primary,
        padding: hasPadding ? calcRem(theme.cards.outerMarginLarge) : undefined,
        paddingBottom: calcRem(7),
        [breakpointMax('sm')]: {
            marginBottom: verticalSpacing && theme.margins[verticalSpacing],
        },
        '&:focus-within': {
            outlineColor: theme.sectionValues.primaryColor,
            outlineOffset: '1px',
            outlineStyle: 'auto',
            outlineWidth: '1px',
        },
    }),
)

export interface StyledLinkProps {
    isLarge?: boolean
    hasBackground?: boolean
    cardOrientation?: BauhausCardOrientationOptions
    hasPadding?: boolean
    fullWidthOnMobile?: boolean
    isFunded?: boolean
}

interface StyledCardMediaAndTextProps {
    cardType?: CardType
}
export const StyledLink = styled(CardTextInteractions, {
    shouldForwardProp: createPropFilter<WebLinkProps>()([
        'isLarge',
        'hasBackground',
        'cardOrientation',
        'hasPadding',
        'fullWidthOnMobile',
        'isFunded',
    ]),
})<StyledLinkProps>(
    {
        textDecoration: 'none',
        display: 'flex',
        flexFlow: 'column nowrap',
        justifyContent: 'space-between',
        width: '100%',
        textDecorationSkipInk: 'auto',
        color: colors.sevennews.bauhausBlack,
    },

    ({ fullWidthOnMobile, hasPadding, theme, isLarge }) => [
        fullWidthOnMobile &&
            !hasPadding && {
                [breakpointMax('sm')]: {
                    padding: isLarge
                        ? calcRem(16)
                        : calcRem(theme.cards.outerMargin),
                },
            },

        {
            '&:focus': {
                outline: `none`,
            },

            '&:hover': {
                [`& ${StyledHeadlineText}`]: {
                    color: 'inherit',
                },
            },
        },
        {
            [breakpoint('sm')]: {},
        },
        isLarge && {
            '& [class*="StyledHeadline"]': {
                fontSize: calcRem(15),
                lineHeight: 1,
                [breakpoint('sm')]: {
                    fontSize: calcRem(27),
                    lineHeight: 1,
                },
                [breakpoint('md')]: {
                    fontSize: calcRem(25),
                    lineHeight: 1,
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    display: '-webkit-box',
                },
                '& [class*="StyledHeadlineText"]': {
                    fontSize: 'inherit',
                    lineHeight: calcRem(19),
                    [breakpoint('sm')]: {
                        lineHeight: calcRem(30),
                    },
                    [breakpoint('md')]: {
                        lineHeight: calcRem(33),
                    },
                },
                '& [class*="StyledKicker"]': {
                    display: 'flex',
                    alignItems: 'center',
                    '& [class*="StyledKickerText"]': {
                        [breakpoint('md')]: {
                            fontSize: calcRem(16),
                        },
                    },
                },
            },
            '& [class*="StyledTeaser"]': {
                fontWeight: 300,
                fontSize: calcRem(14),
                lineHeight: calcRem(18),
                marginTop: calcRem(4),
                [breakpoint('sm')]: {
                    fontSize: calcRem(20),
                    lineHeight: calcRem(22),
                },
                [breakpoint('md')]: {
                    fontSize: calcRem(21),
                    lineHeight: calcRem(26),
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    display: '-webkit-box',
                },
            },
        },
    ],
)
StyledLink.displayName = 'StyledLink'
export const StyledCardMediaAndText = styled(
    'div',
)<StyledCardMediaAndTextProps>(({ cardType }) => [
    cardType === 'small' && {
        display: 'flex',
        flexDirection: 'row-reverse',

        div: {
            flex: 1,
        },
        [`${StyledEditorialLabel}`]: {
            bottom: calcRem(17),
            fontSize: calcRem(16),
            padding: calcRem(3, 8, 3, 5),
        },
        '& [class*="StyledTeaser"]': {
            fontWeight: 300,
            fontSize: calcRem(14),
            lineHeight: calcRem(18),
            [breakpoint('sm')]: {
                fontSize: calcRem(20),
                lineHeight: calcRem(22),
                marginTop: calcRem(6),
            },
            [breakpoint('md')]: {
                fontSize: calcRem(21),
                lineHeight: calcRem(26),
                marginTop: calcRem(6),
            },
        },
    },
    cardType === 'medium' && {
        '& [class*="StyledTeaser"]': {
            fontWeight: 300,
            fontSize: calcRem(14),
            lineHeight: calcRem(16),
            [breakpoint('sm')]: {
                fontSize: calcRem(13),
                lineHeight: calcRem(14),
                marginTop: calcRem(6),
            },
            [breakpoint('md')]: {
                fontSize: calcRem(15),
                lineHeight: calcRem(18),
                marginTop: calcRem(6),
            },
        },
    },
])
interface StyledCardMediaProps {
    isLarge?: boolean
    hasBackground?: boolean
    cardOrientation?: BauhausCardOrientationOptions
    mediaMode?: MediaMode
    fullWidthOnMobile?: boolean
}

const mobileImageWidth = 110
const tabletImageWidth = 210

export const StyledCardMediaWrapper = styled('div')(() => ({
    position: 'relative',
}))

export const StyledCardMedia = styled(CardMedia)<StyledCardMediaProps>(
    ({ cardOrientation }) => ({
        // Different image widths based on the Breakpoint for the card
        ...(cardOrientation && cardOrientation.type === 'Landscape-bauhaus'
            ? {
                  [breakpointMax('sm')]: {
                      width: '100%',
                      alignSelf: 'flex-start', //ensures image container does not stretch to height of text column breaking interaction states
                      margin: calcRem(0, metrics.sevennews.margins.sm, 0, 0),
                  },
              }
            : {}),

        ...(cardOrientation &&
        cardOrientation.type === 'Landscape-bauhaus' &&
        cardOrientation.maxBreakpoint === 'md'
            ? {
                  [breakpointBetween('sm', 'md')]: {
                      maxWidth: tabletImageWidth,
                      width: '100%',
                      marginRight: calcRem(metrics.sevennews.margins.md),
                  },
              }
            : {}),
    }),
    ({ mediaMode }) =>
        mediaMode === 'edgeToEdge' && {
            [breakpoint('sm')]: {
                width: `calc(100% + ${
                    metrics.sevennews.cards.outerMarginLarge * 2
                }px)`,
                margin: calcRem(
                    -metrics.sevennews.cards.outerMarginLarge,
                    -metrics.sevennews.cards.outerMarginLarge,
                    metrics.sevennews.margins.sm,
                    -metrics.sevennews.cards.outerMarginLarge,
                ),
            },
        },

    ({ fullWidthOnMobile }) =>
        fullWidthOnMobile && {
            [breakpointMax('sm')]: {
                width: '100%',
                margin: calcRem(0, 0, metrics.sevennews.margins.sm, 0),
            },
        },
)

//obrisi
export const StyledEditorialLabel = styled(EditorialLabel)({
    position: 'absolute',
    bottom: calcRem(8),
    left: calcRem(8),
    background: colors.sevennews.red,
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '2px',
    padding: calcRem(5, 16, 5, 10),
    [`${StyledLiveBubble}`]: {
        width: calcRem(7),
        height: calcRem(7),
        marginRight: calcRem(3),
    },
})

export const StyledTopicName = styled('span')(({ theme }) => ({
    color: theme.sectionValues.primaryColor,
    fontSize: calcRem(11),
    lineHeight: calcRem(14),
    fontWeight: 900,
    textTransform: 'uppercase',
}))

interface StyledCardTextProps {
    cardOrientation?: BauhausCardOrientationOptions
    fullWidthOnMobile?: boolean
    teaserMode: TeaserMode
    isLarge?: boolean
    cardType?: CardType
}

export const StyledCardText = styled(CardText)<StyledCardTextProps>(
    ({ cardOrientation, fontScale, isLarge, cardType, theme }) => ({
        [`${StyledHeadlineText}`]: {
            fontFamily: fonts.sevennews.headingAlternative,
        },
        [`${StyledKickerText}`]: {
            color: theme.sectionValues.primaryColor,
            lineHeight: '15px',
            fontSize: calcRem(11),
        },
        [`& .${StyledTeaser}`]: {
            color: colors.sevennews.red,
        },
        ...(cardOrientation && cardOrientation.type === 'Landscape-bauhaus'
            ? {
                  [breakpointMax('xs')]: {
                      [`& .${StyledTeaser}`]: {
                          display: 'none',
                      },
                  },
              }
            : {}),

        ...(cardOrientation &&
        cardOrientation.type === 'Landscape-bauhaus' &&
        cardOrientation.maxBreakpoint === 'md'
            ? {
                  // Stops breakpoints being overwritten
                  [breakpointBetween('sm', 'md')]: {
                      //   width: `calc(100% - (${
                      //       tabletImageWidth + metrics.sevennews.margins.md
                      //   }px))`,

                      [`& ${StyledHeadlineText}`]: {
                          fontSize:
                              fontScale && calcRem(Math.ceil(18 * fontScale)),
                      },
                  },
              }
            : {}),

        ...(isLarge && {
            color: 'red',
            [breakpoint('md')]: {
                [`& ${StyledHeadlineText}`]: {
                    fontSize: calcRem(30),
                },
            },
        }),
        ...(cardType &&
            cardType === 'small' && {
                [breakpoint('md')]: {
                    [`& ${StyledHeadlineText}`]: {
                        lineHeight: calcRem(16),
                    },
                },
                paddingRight: calcRem(15),
                [`${StyledKicker}`]: {
                    display: 'flex',
                    alignItems: 'center',
                    [`${StyledLiveStatus}`]: {
                        fontSize: calcRem(10),
                        padding: calcRem(3, 10),
                        marginRight: calcRem(7),
                    },
                    [`${StyledPulsatingDotLogo}`]: {
                        width: calcRem(5),
                        height: calcRem(5),
                    },
                },
            }),
        ...(cardType &&
            cardType === 'medium' && {
                [`& ${StyledHeadlineText}`]: {
                    fontSize: calcRem(19),
                },
                [breakpoint('sm')]: {
                    [`& ${StyledHeadlineText}`]: {
                        fontSize: calcRem(16),
                    },
                },
                [breakpoint('md')]: {
                    [`& ${StyledHeadlineText}`]: {
                        fontSize: calcRem(19),
                    },
                },
            }),
    }),
)

export const StyledPlayButtonWrapper = styled('div')({
    width: `calc(100% - ${calcRem(9)})`,
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    '& [class*="StyledPlayButton"]': {
        position: 'unset',
        marginTop: 0,
        marginLeft: 0,
    },
    [breakpoint('sm')]: {
        height: `calc(100% - ${calcRem(9)})`,
        width: '100%',
    },
})

export const StyledBauhausCardListItemWrapper = styled('li')({
    listStyle: 'none',
})
